import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Button } from 'components/Button';
import Loading from 'components/Loading';
import { Modal } from 'components/Modal';
import { usePrintManager } from 'hooks/usePrintManager';
import { usePrintReceipt } from 'hooks/usePrintReceipt';
import { usePrintTickets } from 'hooks/usePrintTickets';
import { useCart } from 'shared/CartContext';

const Container = styled.div`
  padding: 20px;
  justify-content: center;
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const StatusText = styled.p`
  font-size: 16px;
  color: #333;
  text-align: center;
  margin: 0;
`;

const TroubleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background-color: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 8px;
  margin-top: 10px;
`;

const TroubleText = styled.p`
  font-size: 14px;
  color: #856404;
  margin: 0;
  text-align: center;
`;

const STATUS_CONNECTED = 'Connected';
const TIMEOUT_DURATION = 12000; // 12 seconds

export const PrintingModal = ({
  isVisible,
  printSettingEnabled,
  printReceiptEnabled,
  printReceiptWithTickets,
  setOpenSuccessModal,
}) => {
  const [showTrouble, setShowTrouble] = useState(true);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const timeoutRef = useRef(null);

  const {
    setConnectionStatus,
    setIsPrinting,
    currentStatus,
    setCurrentStatus,
  } = useCart();

  const IPAddress = localStorage.getItem('readerIp');
  const { connect, isConnected } = usePrintManager();
  const { reprintLastReceipt } = usePrintReceipt();
  const { reprintLastTransaction } = usePrintTickets();

  const clearTimeout = () => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const startTimeout = () => {
    clearTimeout();
    setShowTrouble(false);

    timeoutRef.current = setTimeout(() => {
      setShowTrouble(true);
      setCurrentStatus('Print operation taking longer than expected...');
    }, TIMEOUT_DURATION);
  };

  const reprintPurchase = async () => {
    try {
      setCurrentStatus('Printing...');
      startTimeout();

      if (printSettingEnabled) {
        setCurrentStatus('Printing tickets...');
        await reprintLastTransaction(setIsPrinting);

        // Print receipt with tickets only if the setting for printing tickets is enabled
        if (printReceiptWithTickets) {
          setCurrentStatus('Printing receipt...');
          await reprintLastReceipt(setIsPrinting);
        }
      }

      if (printReceiptEnabled) {
        setCurrentStatus('Printing receipt...');
        await reprintLastReceipt(setIsPrinting);
      }

      // If we get here, printing was successful
      clearTimeout();
      setCurrentStatus('Print completed successfully!');

      // Close modal after brief success message
      setTimeout(() => {
        setIsPrinting(false);
        setShowTrouble(false);
        setCurrentStatus('Printing...');

        // Proceed to next steps or close modal if needed
        if (setOpenSuccessModal) setOpenSuccessModal(false);
      }, 1000);
    } catch (error) {
      clearTimeout();
      setShowTrouble(true);
      setCurrentStatus('Print operation failed. Please try reconnecting.');
      console.error('Print error:', error);
    }
  };

  const connectPrinter = async () => {
    try {
      setCurrentStatus('Connecting to printer...');
      setConnectionStatus('Connecting ...');

      if (isConnected()) {
        setConnectionStatus(STATUS_CONNECTED);
        await reprintPurchase();
        return;
      }

      const result = await connect(
        IPAddress,
        setConnectionStatus,
        setIsPrinting
      );

      if (result) {
        setConnectionStatus(STATUS_CONNECTED);
        setCurrentStatus('Connected! Starting print...');
        await reprintPurchase();
      } else {
        setShowTrouble(true);
        setCurrentStatus('Failed to connect to printer');
        alert('Issue connecting to printer');
        return false;
      }
    } catch (error) {
      setShowTrouble(true);
      setCurrentStatus('Connection error occurred');
      alert('Error connecting to printer: ' + error.message);
      return false;
    }
  };

  const handleReconnect = async () => {
    setIsReconnecting(true);
    setShowTrouble(true);

    try {
      await connectPrinter();
    } finally {
      setIsReconnecting(false);
    }
  };

  const handleCancel = () => {
    clearTimeout();
    setIsPrinting(false);
    setShowTrouble(false);
    setCurrentStatus('Printing...');
    setIsReconnecting(false);

    // Proceed to next steps or close modal if needed
    if (setOpenSuccessModal) setOpenSuccessModal(false);
  };

  // Set up timeout when modal becomes visible and print settings are passed in
  useEffect(() => {
    if (
      isVisible &&
      (printSettingEnabled || printReceiptEnabled || printReceiptWithTickets)
    ) {
      startTimeout();
      if (isConnected()) setCurrentStatus('Printing...');
    } else {
      clearTimeout();
      setShowTrouble(false);
      setCurrentStatus('Printing...');
      setIsReconnecting(false);
    }

    // Cleanup on unmount
    return () => {
      clearTimeout();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      clearTimeout();
    };
  }, []);

  return (
    <Modal
      isVisible={isVisible}
      title="Print Status"
      maxWidth={600}
      closable={showTrouble}
      onClose={showTrouble ? handleCancel : undefined}
    >
      <Container>
        {!showTrouble ? (
          <LoadingContainer>
            <Loading size={30} />
            <StatusText>{currentStatus}</StatusText>
          </LoadingContainer>
        ) : (
          <>
            <StatusText>{currentStatus}</StatusText>
            <TroubleContainer>
              <TroubleText>
                <strong>Having Trouble?</strong>
                <br />
                The print operation is taking longer than expected. You can try
                reconnecting to the printer and attempt printing again.
              </TroubleText>
              <div
                style={{ display: 'flex', gap: '12px', alignItems: 'center' }}
              >
                <Button onClick={handleReconnect} disabled={isReconnecting}>
                  {isReconnecting
                    ? 'Reconnecting...'
                    : 'Click here to reconnect'}
                </Button>
                <Button onClick={handleCancel} outlined={true}>
                  Cancel
                </Button>
              </div>
            </TroubleContainer>
          </>
        )}
      </Container>
    </Modal>
  );
};
